import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { DashboardMockup } from "@/components/landing/DashboardMockup";
import { useStartFlow } from "@/lib/useStart";

export const Hero = ({ onSeeGuarantee, onSeeHow }) => {
  const start = useStartFlow();

  return (
    <section className="relative overflow-hidden bg-[#040406] text-white pt-28 pb-24 md:pt-36 md:pb-32" data-testid="hero-section">
      <div className="absolute inset-0 hero-radial" />
      <div className="absolute inset-0 grid-lines opacity-40" />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* announcement */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs sm:text-sm text-white/70 backdrop-blur-md">
            <Sparkles className="h-3.5 w-3.5 text-amber-300" />
            Toppers don't study harder. They study in the right order.
          </div>
        </motion.div>

        <div className="text-center max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05]"
          >
            Your Exam Has a Date.
            <br />
            Your Preparation Needs a <span className="text-gradient">Pilot.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed"
          >
            Every topper has a system. sarkarijobai builds yours — turning your scores, mistakes,
            weak topics and study hours into the single highest-scoring move you should make next.
            Every single day, until exam day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={() => start("hero-primary")}
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium px-7 py-3.5 shadow-[0_0_30px_rgba(250,204,21,0.35)] hover:shadow-[0_0_45px_rgba(250,204,21,0.55)] hover:scale-105 transition-all"
              data-testid="hero-get-started"
            >
              Get Started
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={onSeeHow}
              className="rounded-full border border-white/20 bg-white/5 text-white font-medium px-7 py-3.5 hover:bg-white/10 transition-colors"
              data-testid="hero-see-how"
            >
              See How It Works
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-5 text-xs uppercase tracking-[0.25em] text-white/40"
          >
            AI-powered · Personalized · Rank-focused
          </motion.p>
        </div>

        {/* dashboard visual */}
        <div className="mt-20 md:mt-24">
          <DashboardMockup />
        </div>
      </div>

      {/* guarantee strip */}
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="rounded-2xl border border-amber-400/25 bg-gradient-to-r from-amber-500/10 to-red-600/10 p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 shrink-0 rounded-xl bg-amber-400/15 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-amber-300" />
            </div>
            <p className="text-sm sm:text-base text-white/85 font-medium leading-relaxed max-w-2xl">
              We're confident enough to back it with money. Follow the program, meet the eligibility
              criteria, and if you don't hit the defined qualifying outcome — you may get your money back.
            </p>
          </div>
          <button
            onClick={onSeeGuarantee}
            className="shrink-0 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-white/5 px-5 py-2.5 text-sm font-medium text-amber-200 hover:bg-amber-400/10 transition-colors"
            data-testid="hero-see-guarantee"
          >
            See Guarantee Terms
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};
